/**
 * Serials — the five characters on every label.
 *
 * Design guide §9.1. Three properties, each of which is load-bearing:
 *
 *  - **Type-agnostic.** A serial says nothing about what it is attached to, so
 *    a roll of 500 labels can be printed with no knowledge of what they will go
 *    on. The type arrives at intake, when the part is scanned (§9.3).
 *  - **Unambiguous when read aloud or typed.** Crockford's base32 alphabet
 *    omits I, L, O and U, so there is no 0/O and no 1/I/L confusion on a scuffed
 *    label. Decoding is forgiving in the one direction that matters: someone
 *    typing `O` meant `0`.
 *  - **Self-checking.** The last character is computed from the other four, so a
 *    mistyped serial is rejected at the keyboard rather than becoming a lookup
 *    for a different real part. That second failure is the dangerous one: it
 *    silently attributes a measurement to the wrong actuator.
 *
 * The design guide's `K7F3Q` is illustrative; a real serial's fifth character is
 * whatever `checkCharacter` computes for the first four.
 */

/** Crockford base32. No I, L, O (readability) and no U (avoids accidental words). */
export const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'

/** Four data characters plus one check character. */
export const DATA_LENGTH = 4
export const SERIAL_LENGTH = DATA_LENGTH + 1

/** 32^4. The size of the pool before the format has to grow a character. */
export const POOL_SIZE = 32 ** DATA_LENGTH

/**
 * Odd weights, and that is the whole reason they are these numbers.
 *
 * A single wrong character shifts the weighted sum by `weight * delta`. With an
 * even weight, `2 * 16 ≡ 0 (mod 32)` — a wrong character that would not be
 * caught. Every weight here is coprime to 32, so **every** single-character
 * error changes the check character.
 *
 * Adjacent transpositions shift the sum by `2 * (a - b)`, which vanishes when
 * the two characters' values differ by exactly 16. That one class of
 * transposition is not caught; it is the accepted cost of keeping the check
 * character inside the same 32-character alphabet, so the label needs no symbol
 * that a QR alphanumeric payload or a person reading it aloud would stumble on.
 */
const WEIGHTS = [1, 3, 5, 7] as const

export type Serial = string & { readonly __brand: 'Serial' }

/** Crockford's decoding rules: case-insensitive, and O/I/L are digit typos. */
function decodeChar(c: string): number {
  const u = c.toUpperCase()
  if (u === 'O') return 0
  if (u === 'I' || u === 'L') return 1
  const i = ALPHABET.indexOf(u)
  return i
}

/**
 * What a scanner, a keyboard or a QR payload might hand us, reduced to the
 * canonical form. Hyphens and spaces are stripped because people insert them.
 */
export function normalize(raw: string): string {
  return raw.trim().replace(/[\s-]/g, '').toUpperCase()
}

/** The check character for four data characters. Throws on anything else. */
export function checkCharacter(data: string): string {
  if (data.length !== DATA_LENGTH) {
    throw new Error(`a serial's data part is ${DATA_LENGTH} characters, got ${data.length}`)
  }
  let sum = 0
  for (let i = 0; i < DATA_LENGTH; i += 1) {
    const value = decodeChar(data[i] as string)
    if (value < 0) throw new Error(`${data[i]} is not a base32 character`)
    sum += value * (WEIGHTS[i] as number)
  }
  return ALPHABET[sum % 32] as string
}

/**
 * The nth serial in the pool. Sequential, because the pool is printed in order
 * (§9.1) — not random, which would need a uniqueness check per label.
 */
export function encodeSerial(n: number): Serial {
  if (!Number.isInteger(n) || n < 0 || n >= POOL_SIZE) {
    throw new Error(`serial index must be an integer in [0, ${POOL_SIZE}), got ${n}`)
  }
  let data = ''
  for (let i = DATA_LENGTH - 1; i >= 0; i -= 1) {
    data += ALPHABET[(n >> (5 * i)) & 31] as string
  }
  return (data + checkCharacter(data)) as Serial
}

/** The inverse of `encodeSerial`, for "what position in the pool is this?". */
export function serialIndex(serial: Serial): number {
  let n = 0
  for (let i = 0; i < DATA_LENGTH; i += 1) {
    n = n * 32 + decodeChar(serial[i] as string)
  }
  return n
}

export type ParsedSerial =
  | { readonly ok: true; readonly serial: Serial }
  | { readonly ok: false; readonly reason: 'length' | 'alphabet' | 'check' }

/**
 * Parse anything a scan or a keyboard produced.
 *
 * Returns a reason rather than throwing, because all three failures are things
 * a person does and each deserves a different message on screen.
 */
export function parseSerial(raw: string): ParsedSerial {
  const s = normalize(raw)
  if (s.length !== SERIAL_LENGTH) return { ok: false, reason: 'length' }

  let canonical = ''
  for (const c of s) {
    const v = decodeChar(c)
    if (v < 0) return { ok: false, reason: 'alphabet' }
    canonical += ALPHABET[v] as string
  }

  const data = canonical.slice(0, DATA_LENGTH)
  if (checkCharacter(data) !== canonical[DATA_LENGTH]) return { ok: false, reason: 'check' }
  return { ok: true, serial: canonical as Serial }
}

/**
 * The QR payload (§9.1): an uppercase URL, so the code stays in alphanumeric
 * mode. `HTTPS://KRONOS.NUPEAK.TOOLS/K7F38` is 33 characters, inside the
 * 38-character capacity of a version-2 code at error-correction level M — which
 * is the 25x25 grid the 9 mm label budget assumes. Lookup is case-insensitive
 * server-side, so the uppercase costs nothing.
 *
 * Verify a generated code against the real printer before committing a roll:
 * the capacity table leaves no margin if the host name ever grows.
 */
export function qrPayload(host: string, serial: Serial): string {
  return `HTTPS://${host.toUpperCase()}/${serial}`
}
